import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateBrandingQuestionnaireTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const contactInfoId = uuidv4();
  const contactInfoBlockId = uuidv4();
  const emailId = uuidv4();
  const emailBlockId = uuidv4();
  const multipleChoice1Id = uuidv4();
  const multipleChoice1BlockId = uuidv4();
  const websiteId = uuidv4();
  const websiteBlockId = uuidv4();
  const dropdown1Id = uuidv4();
  const dropdown1BlockId = uuidv4();
  const multipleChoice2Id = uuidv4();
  const multipleChoice2BlockId = uuidv4();
  const dropdown2Id = uuidv4();
  const dropdown2BlockId = uuidv4();
  const dateId = uuidv4();
  const dateBlockId = uuidv4();
  const longTextId = uuidv4();
  const longTextBlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for select options - MC1 (industry)
  const mc1Option1Id = uuidv4(); // Hospitality & Food
  const mc1Option2Id = uuidv4(); // Health & Wellness
  const mc1Option3Id = uuidv4(); // E-commerce
  const mc1Option4Id = uuidv4(); // Professional Services

  // Generate new UUIDs for select options - Dropdown1 (brand goal)
  const dropdown1Option1Id = uuidv4(); // Create from ground up
  const dropdown1Option2Id = uuidv4(); // Refresh existing
  const dropdown1Option3Id = uuidv4(); // New product line

  // Generate new UUIDs for select options - MC2 (services interested)
  const mc2Option1Id = uuidv4(); // Visual identity & logo
  const mc2Option2Id = uuidv4(); // Website design
  const mc2Option3Id = uuidv4(); // Brand storytelling
  const mc2Option4Id = uuidv4(); // Content creation

  // Generate new UUIDs for select options - Dropdown2 (budget)
  const dropdown2Option1Id = uuidv4(); // $4,000–$8,000
  const dropdown2Option2Id = uuidv4(); // $8,000–$15,000
  const dropdown2Option3Id = uuidv4(); // $20,000+

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const emailMediaId = uuidv4();
  const endScreenMediaId = uuidv4();

  return {
    ...themes.navyOrchidGlow,
    "collectionId": collectionId,
    "name": "Branding Questionnaire",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "SMALL",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "Ready to elevate your brand?",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Tell us a bit about your business and your goals.",
        "thirdContent": "Start now",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": welcomeMediaId,
          "type": "image",
          "name": "color code book",
          "link": "https://unsplash.com/photos/LPWl2pEVGKc/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwyfHxicmFuZGluZ3xlbnwwfHx8fDE3NjQ1NzUyMzV8MA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Balázs Kétyi",
          "authorProfile": "https://unsplash.com/@balazsketyi",
          "downloadLink": "https://api.unsplash.com/photos/LPWl2pEVGKc/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwyfHxicmFuZGluZ3xlbnwwfHx8fDE3NjQ1NzUyMzV8MA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": contactInfoId,
        "blockId": contactInfoBlockId,
        "content": "First, what's your name?",
        "manditory": true,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll personalize the journey from here.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": true
          },
          "last_name": {
            "required": false,
            "visible": true
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": false
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "EMAIL",
        "strictness": "COMMON",
        "contentAlignment": "RIGHT",
        "mediaPosition": "SPLIT_LEFT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": emailId,
        "blockId": emailBlockId,
        "content": "What's your email?",
        "manditory": true,
        "order": 2,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll reach out with next steps, no spam, ever.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": emailMediaId,
          "type": "image",
          "name": "black iPad displaying color gradient",
          "link": "https://unsplash.com/photos/FeuEg-8XlA8/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw0fHxicmFuZGluZ3xlbnwwfHx8fDE3NjQ1NzUyMzV8MA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Balázs Kétyi",
          "authorProfile": "https://unsplash.com/@balazsketyi",
          "downloadLink": "https://api.unsplash.com/photos/FeuEg-8XlA8/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw0fHxicmFuZGluZ3xlbnwwfHx8fDE3NjQ1NzUyMzV8MA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice1Id,
        "blockId": multipleChoice1BlockId,
        "content": "What industry are you in?",
        "manditory": true,
        "order": 3,
        "selectOptions": [
          {
            "id": mc1Option1Id,
            "order": 0,
            "value": "Hospitality & Food"
          },
          {
            "id": mc1Option2Id,
            "order": 1,
            "value": "Health & Wellness"
          },
          {
            "id": mc1Option3Id,
            "order": 2,
            "value": "E-commerce"
          },
          {
            "id": mc1Option4Id,
            "order": 3,
            "value": "Professional Services"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Choose the closest match.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "WEBSITE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": websiteId,
        "blockId": websiteBlockId,
        "content": "4. Where can we find your website?",
        "manditory": false,
        "order": 4,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "If you don't have one yet, feel free to skip this; we can help build it.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "DROPDOWN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": dropdown1Id,
        "blockId": dropdown1BlockId,
        "content": "When it comes to your brand, what are you hoping to accomplish?",
        "manditory": true,
        "order": 5,
        "selectOptions": [
          {
            "id": dropdown1Option1Id,
            "order": 0,
            "value": "Create a brand identity from the ground up"
          },
          {
            "id": dropdown1Option2Id,
            "order": 1,
            "value": "Refresh or modernize an existing brand"
          },
          {
            "id": dropdown1Option3Id,
            "order": 2,
            "value": "Develop brand assets for a new product line"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Select the best fit.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice2Id,
        "blockId": multipleChoice2BlockId,
        "content": "Which services are you interested in?",
        "manditory": true,
        "order": 6,
        "selectOptions": [
          {
            "id": mc2Option1Id,
            "order": 0,
            "value": "Visual identity & logo creation"
          },
          {
            "id": mc2Option2Id,
            "order": 1,
            "value": "Website or landing page design"
          },
          {
            "id": mc2Option3Id,
            "order": 2,
            "value": "Brand storytelling & messaging"
          },
          {
            "id": mc2Option4Id,
            "order": 3,
            "value": "Content creation (photo/video/social)"
          }
        ],
        "multiSelect": true,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Pick as many as you'd like.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "DROPDOWN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": dropdown2Id,
        "blockId": dropdown2BlockId,
        "content": "What budget range are you considering?",
        "manditory": true,
        "order": 7,
        "selectOptions": [
          {
            "id": dropdown2Option1Id,
            "order": 0,
            "value": "$4,000–$8,000"
          },
          {
            "id": dropdown2Option2Id,
            "order": 1,
            "value": "$8,000–$15,000"
          },
          {
            "id": dropdown2Option3Id,
            "order": 2,
            "value": "$20,000+"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "DATE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": dateId,
        "blockId": dateBlockId,
        "content": "Do you have a timeline or deadline in mind?",
        "manditory": false,
        "order": 8,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "MMDDYYYY",
        "thirdContent": "/",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": longTextId,
        "blockId": longTextBlockId,
        "content": "Anything else we should know about your project?",
        "manditory": false,
        "order": 9,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Goals, challenges, special requirements; we're all ears.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "content": "That's everything. Thanks!",
        "manditory": false,
        "order": 10,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll be in touch with next steps shortly.",
        "thirdContent": "View Our Services",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 37,
        "media": {
          "provider": "UNSPLASH",
          "id": endScreenMediaId,
          "type": "image",
          "name": "a computer monitor sitting on top of a wooden desk",
          "link": "https://unsplash.com/photos/d7h4kZXmHhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw3fHxicmFuZGluZ3xlbnwwfHx8fDE3NjQ1NzUyMzV8MA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Faizur Rehman",
          "authorProfile": "https://unsplash.com/@fazurrehman",
          "downloadLink": "https://api.unsplash.com/photos/d7h4kZXmHhk/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw3fHxicmFuZGluZ3xlbnwwfHx8fDE3NjQ1NzUyMzV8MA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      }
    ],
    "snippets": [],
    "metaTitle": "Branding Questionnaire",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateBrandingQuestionnaireTemplate;
